/** Formatting for things a reviewer reads off the screen. */

/**
 * Epoch seconds as a date and time.
 *
 * The API sends epoch seconds rather than a formatted string, so this renders
 * in the reader's own timezone. That matters more than it looks: a workpaper
 * generated at 9am in Sydney is the previous day in UTC, and a BAS period is
 * bounded by dates. Showing a date that disagrees with the one on the
 * workpaper would be a recurring "which one is right?" question.
 */
export function formatWhen(epochSeconds: number): string {
  if (!epochSeconds) return "";
  return new Date(epochSeconds * 1000).toLocaleString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/** The same instant, spelled out in full, for a tooltip. */
export function formatWhenLong(epochSeconds: number): string {
  if (!epochSeconds) return "";
  return new Date(epochSeconds * 1000).toLocaleString("en-AU", {
    dateStyle: "full",
    timeStyle: "long",
  });
}

/**
 * How long something has been running, as a preparer would say it.
 *
 * Generation and the GST review take minutes, and a tag that only reads
 * "Generating" tells you nothing about whether to wait or come back. Seconds
 * below a minute, then minutes and seconds, then minutes alone -- a run past
 * ten minutes is one you have stopped watching the seconds of.
 */
export function formatElapsed(seconds: number): string {
  const whole = Math.max(0, Math.floor(seconds));
  if (whole < 60) return `${whole}s`;
  const minutes = Math.floor(whole / 60);
  const rest = whole % 60;
  if (minutes < 10) return rest ? `${minutes}m ${rest}s` : `${minutes}m`;
  return `${minutes}m`;
}
